import AddIcon from '@mui/icons-material/Add';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PeopleIcon from '@mui/icons-material/People';
import { ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import Toolbar from '@mui/material/Toolbar';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ShellToolbar from '../../components/dashboard/ShellToolbar';
import LoadingView from '../../components/general/LoadingView';
import { fetchOwnerUserData, loginFailure, selectAuthLoading, selectUser } from '../../redux/slices/authSlice';
import { selectCurrItem, setCurrItem } from '../../redux/slices/drawerSlice';
import { AppDispatch } from '../../redux/store/store';
import { AppBar, Drawer } from '../../util/styledComponents';
import AllClubEventsPage from './drawer/AllClubEventsPage';
import CreateClubPage from './CreateClubPage';
import CreateEvent from './drawer/CreateEventPage';
import DashBoard from './drawer/DashBoard';
import GuestList from './drawer/GuestList';
import NearEventsListPage from './drawer/NearEventsListPage';
import PushNotificationsPage from './PushNotificationsPage';

type NavItem = {
  label: string;
  icon: React.ReactNode;
  masterOnly?: boolean;
};

const navItems: NavItem[] = [
  { label: 'DASHBOARD', icon: <DashboardIcon /> },
  { label: 'ALL EVENTS', icon: <CalendarMonthIcon /> },
  { label: 'ADD EVENT', icon: <AddIcon /> },
  { label: 'GUESTLIST', icon: <PeopleIcon /> },
  { label: 'NEAR EVENTS', icon: <LocalBarIcon /> },
  { label: 'PUSH NOTIFICATIONS', icon: <NotificationsActiveIcon />, masterOnly: true },
  { label: 'ADD CLUB', icon: <AddBusinessIcon />, masterOnly: true },
];

export default function OverViewPage() {
  const auth = getAuth();
  const dispatch: AppDispatch = useDispatch();
  const isLoading = useSelector(selectAuthLoading);
  const user = useSelector(selectUser);
  const [open, setOpen] = React.useState(false);
  const currItem = useSelector(selectCurrItem);
  const navigate = useNavigate();
  const isMaster = user?.userType?.toUpperCase() === 'MASTER';
  const visibleNavItems = navItems.filter((item) => !item.masterOnly || isMaster);
  const toggleDrawer = () => {
    setOpen(!open);
  };

  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const uid = user.uid;
        const token = await user.getIdToken();
        if (uid) {
          dispatch(fetchOwnerUserData({ uid: uid, token: token }));
        } else {
          dispatch(loginFailure(""))
        }
      } else {
        // User is signed out
        dispatch(loginFailure(""));
        navigate("/login")
        console.log("user is logged out")
      }
    });
    return () => {
      unsubscribe();
    };
  }, []);

  React.useEffect(() => {
    if (!isMaster && (currItem === 'PUSH NOTIFICATIONS' || currItem === 'ADD CLUB')) {
      dispatch(setCurrItem('ALL EVENTS'));
    }
  }, [isMaster, currItem, dispatch]);

  if (isLoading) {
    return <LoadingView />
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="absolute" open={open}>
        <ShellToolbar open={open} setOpen={setOpen} />
      </AppBar>
      <Drawer variant="permanent" open={open}>
        <Toolbar
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            px: [1],
          }}
        >
          <IconButton onClick={toggleDrawer}>
            <ChevronLeftIcon />
          </IconButton>
        </Toolbar>
        <Divider />
        <List component="nav">
          {visibleNavItems.map((item) => (
            <ListItemButton
              key={item.label}
              selected={currItem === item.label}
              onClick={() => dispatch(setCurrItem(item.label))}
            >
              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          ))}
        </List>
      </Drawer>
      {currItem === "DASHBOARD" && <DashBoard />}
      {currItem === "ALL EVENTS" && <AllClubEventsPage />}
      {currItem === "ADD EVENT" && <CreateEvent />}
      {currItem === "GUESTLIST" && <GuestList />}
      {/* {currItem === "ALL PROMOTERS" && <PromotersListPage />} */}
      {currItem === "NEAR EVENTS" && <NearEventsListPage />}
      {currItem === "PUSH NOTIFICATIONS" && isMaster && <PushNotificationsPage />}
      {currItem === "ADD CLUB" && isMaster && <CreateClubPage />}
    </Box>
  );
}
